import { SeoHead } from "@/components/SeoHead";
import { Link } from "wouter";

export default function TenderGuidePage() {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="香港學校採購與招標實務指南 — 採購金額界線、流程及最佳實踐"
        description="本指南參考香港教育局《學校採購程序指引》，詳細說明全港中、小學及特殊學校的採購金額界線（$5,000 / $50,000 / $200,000）、公開招標五大步驟，以及電子採購平台如何提升透明度與效率。"
        canonical="/tender-guide"
        ogImage="https://www.skoltend.com/tender-guide-hero.jpg"
      />

      {/* Hero */}
      <div className="border-b">
        <div className="relative w-full overflow-hidden" style={{ maxHeight: "360px" }}>
          <img
            src="/tender-guide-hero.jpg"
            alt="香港學校採購辦公室文件桌面，展示招標文件與採購資料夾，象徵學校公開招標及採購程序"
            className="w-full object-cover object-center"
            style={{ maxHeight: "360px" }}
            width={1200}
            height={630}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
        </div>
        <div className="bg-secondary/30 py-8">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl">
            <p className="text-xs font-medium text-primary uppercase tracking-widest mb-2">採購指南</p>
            <h1 className="text-2xl md:text-3xl font-bold mb-2 leading-snug">
              香港學校採購與招標實務指南
            </h1>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">
              本指南參考香港教育局（EDB）的《學校採購程序指引》，為全港中學、小學及特殊學校，
              以及有意承接學校工程與服務的供應商，提供清晰的採購流程與標準規範說明。
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 py-10 max-w-3xl space-y-12 text-sm leading-relaxed text-foreground">

        {/* Section 1 */}
        <section className="space-y-5">
          <h2 className="text-lg font-bold border-b pb-2">1. 學校採購程序及金額界線</h2>
          <p>
            為確保公平公開及符合成本效益，學校必須根據採購項目的預算金額，採取不同的報價或招標方式。
            以下為一般資助學校的採購金額界線標準：
          </p>

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/60 text-left">
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">採購金額（港元）</th>
                  <th className="px-4 py-3 font-semibold">採購方式要求</th>
                  <th className="px-4 py-3 font-semibold">skoltend.com 如何協助</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium whitespace-nowrap">$5,000 以下</td>
                  <td className="px-4 py-3 text-muted-foreground">可透過口頭報價，建議由教職員直接採購。</td>
                  <td className="px-4 py-3 text-muted-foreground">不適用</td>
                </tr>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium whitespace-nowrap">$5,001 – $50,000</td>
                  <td className="px-4 py-3 text-muted-foreground">必須邀請最少 <strong>兩間</strong> 供應商提供書面報價。</td>
                  <td className="px-4 py-3 text-muted-foreground">供應商可於平台尋找小額採購機會。</td>
                </tr>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium whitespace-nowrap">$50,001 – $200,000</td>
                  <td className="px-4 py-3 text-muted-foreground">必須邀請最少 <strong>五間</strong> 供應商提供書面報價。</td>
                  <td className="px-4 py-3 text-muted-foreground">學校可透過平台集中發佈報價邀請，快速配對五間合資格承辦商。</td>
                </tr>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium whitespace-nowrap">$200,000 以上</td>
                  <td className="px-4 py-3 text-muted-foreground">必須進行 <strong>公開招標</strong>，邀請最少五間供應商入標。</td>
                  <td className="px-4 py-3 text-muted-foreground">學校可發佈完整招標公告；供應商可訂閱最新高額招標提示。</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            ※ 具體採購指引請以教育局最新發佈的《學校行政手冊》及相關通告為準。
          </p>
        </section>

        {/* Section 2 */}
        <section className="space-y-5">
          <h2 className="text-lg font-bold border-b pb-2">2. 學校公開招標標準流程</h2>
          <p>
            一項完整的學校公開招標，通常包含以下五個核心步驟：
          </p>

          <div className="space-y-4">
            {[
              {
                step: "01",
                title: "擬定招標文件（Tender Document Preparation）",
                body: "學校需準備詳細的項目規格（Specifications）、服務條款及評審準則。清晰的規格說明有助減少供應商查詢，並確保收到的標書具可比性。",
              },
              {
                step: "02",
                title: "發佈招標公告（Tender Invitation）",
                body: "傳統上學校需於報章或校網登報。現在，學校可利用 skoltend.com 學校招標配對平台，將招標公告統一發佈至全港供應商網絡，擴大接觸面。",
              },
              {
                step: "03",
                title: "實地考察與簡報（Site Visit / Briefing Session）",
                body: "視乎工程或服務性質（如校舍維修、IT 基礎建設），安排有意投標的承辦商到校視察，確保各方對項目範圍有一致理解。",
              },
              {
                step: "04",
                title: "收集及開啟標書（Tender Collection & Opening）",
                body: "於截標日期前收集密封標書，並由校方指定的開標委員會（Tender Opening Committee）統一開啟及記錄，確保過程透明公正。",
              },
              {
                step: "05",
                title: "評審及批核（Tender Evaluation & Approval）",
                body: "由評審委員會根據既定的評分機制（如價格及質素比例）進行評估，並向校董會或法團校董會提交建議及獲取批准，方可正式簽約。",
              },
            ].map(({ step, title, body }) => (
              <div key={step} className="flex gap-4 rounded-lg border p-4 hover:bg-muted/20 transition-colors">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                  {step}
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-sm">{title}</h3>
                  <p className="text-muted-foreground text-sm">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3 */}
        <section className="space-y-5">
          <h2 className="text-lg font-bold border-b pb-2">3. 提升學校招標效率的最佳實踐</h2>
          <p>
            傳統的書面報價與招標過程往往耗費大量行政時間。透過引入電子化採購平台，學校可以：
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                title: "擴闊供應商名冊",
                body: "不再局限於相熟承辦商，透過 skoltend.com 接觸更多具質素的註冊供應商。",
              },
              {
                title: "提高透明度與公平性",
                body: "所有招標資訊統一公開發佈，符合教育局對防貪及利益衝突的管理要求。",
              },
              {
                title: "簡化資訊發佈流程",
                body: "一站式上載招標文件、截標日期及簡報會詳情，節省行政人員時間。",
              },
            ].map(({ title, body }) => (
              <div key={title} className="rounded-lg border p-4 space-y-2 bg-card">
                <h3 className="font-semibold text-sm">{title}</h3>
                <p className="text-muted-foreground text-sm">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="rounded-xl bg-primary/5 border border-primary/20 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <p className="font-semibold">立即瀏覽最新學校招標</p>
            <p className="text-muted-foreground text-xs">每日更新全港中小學公開招標公告，免費登記即可查閱。</p>
          </div>
          <Link href="/tenders">
            <button className="shrink-0 bg-primary text-primary-foreground text-sm font-medium px-5 py-2.5 rounded-lg hover:opacity-90 transition-opacity">
              查看招標列表 →
            </button>
          </Link>
        </div>

      </div>
    </div>
  );
}
